// CompareRacers.js
import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast";
import { FaArrowLeft, FaTrophy, FaChartLine, FaTimes, FaPlus, FaChevronLeft } from "react-icons/fa";
import { loadRacerGroupFilters, saveRacerGroupFilters } from "../utils/persistentFilters";
import GroupFilterDropdown from "./GroupFilterDropdown";
import FilterDropdown from "./FilterDropdown";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../App.css";

// Register Chart.js components
Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

const CompareRacers = () => {
  const { racerIds } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [racers, setRacers] = useState([]);
  const [allRacers, setAllRacers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventGroups, setSelectedEventGroups] = useState(new Set());
  const [selectedEventTracks, setSelectedEventTracks] = useState(new Set());
  const [showGroupFilter, setShowGroupFilter] = useState(false);
  const [showTrackFilter, setShowTrackFilter] = useState(false);
  const [showAddRacerDropdown, setShowAddRacerDropdown] = useState(false);
  const [chartMetric, setChartMetric] = useState("winning"); // "winning" | "median" | "median-absolute" | "median-winning" | "median-median"
  const [pendingChartMetric, setPendingChartMetric] = useState(null);
  const [selectedGroupFilters, setSelectedGroupFilters] = useState([]); // Array of group names
  const [filtersRestored, setFiltersRestored] = useState(false);

  const racerIdArray = useMemo(() => (racerIds ? racerIds.split(",") : []), [racerIds]);

  // Fetch all racers for add/remove functionality
  useEffect(() => {
    const fetchAllRacers = async () => {
      try {
        const racersRef = collection(db, "timer_racers");
        const racersSnapshot = await getDocs(racersRef);
        const allRacersList = racersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllRacers(allRacersList);
      } catch (error) {
        console.error("Error fetching all racers:", error);
      }
    };
    fetchAllRacers();
  }, []);

  // Fetch selected racer data
  useEffect(() => {
    const fetchRacers = async () => {
      if (racerIdArray.length === 0) {
        setRacers([]);
        return;
      }

      try {
        const selectedRacers = allRacers.filter((racer) =>
          racerIdArray.includes(racer.id)
        );
        setRacers(selectedRacers);
      } catch (error) {
        console.error("Error fetching racers:", error);
        showToast("Failed to load racers.");
      }
    };

    fetchRacers();
  }, [racerIds, allRacers, showToast]);

  // Fetch all events
  useEffect(() => {
    const fetchEvents = async () => {
      if (racerIdArray.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const eventsRef = collection(db, "events");
        const eventsSnapshot = await getDocs(eventsRef);
        const allEvents = eventsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter events where ANY selected racer participated
        const relevantEvents = allEvents.filter((event) => {
          if (!event.racers || !Array.isArray(event.racers)) return false;
          const participatingRacerIds = event.racers.map((r) => r.racerId);
          // Check if any selected racer is in this event
          return racerIdArray.some((racerId) =>
            participatingRacerIds.includes(racerId)
          );
        });

        setEvents(relevantEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
        showToast("Failed to load events.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [racerIdArray, showToast]);

  // Get distinct event groups from events
  const eventGroups = useMemo(() => {
    const groups = new Set();
    events.forEach((event) => {
      // Check for sharedGroupName or any group field
      if (event.sharedGroupName) {
        groups.add(event.sharedGroupName);
      }
      // Also check if event has a groups array
      if (event.groups && Array.isArray(event.groups)) {
        event.groups.forEach((g) => groups.add(g));
      }
    });
    return Array.from(groups).sort();
  }, [events]);

  const eventTracks = useMemo(() => {
    const tracks = new Set();
    events.forEach((event) => {
      const trackName = (event.track || event.trackName || "").trim();
      if (trackName) {
        tracks.add(trackName);
      }
    });
    return Array.from(tracks).sort();
  }, [events]);

  // Calculate comparison data
  const comparisonData = useMemo(() => {
    if (racers.length === 0 || events.length === 0) {
      return {
        sharedEvents: [],
        stats: {},
        chartData: null,
      };
    }

    const sharedEvents = [];
    const stats = {};
    const chartDataPoints = {};

    // Initialize stats and chart data for each racer
    racers.forEach((racer) => {
      stats[racer.id] = {
        name: racer.name,
        wins: 0,
        losses: 0,
        ties: 0,
        totalEvents: 0,
        averagePosition: 0,
        averageTime: 0,
        bestTime: null,
        bestPosition: null,
        totalTime: 0,
        totalPosition: 0,
      };
      chartDataPoints[racer.id] = [];
    });

    // Filter events by selected groups
    const groupFilteredEvents = events.filter((event) => {
      if (selectedEventGroups.size === 0) return true;
      
      const eventGroupNames = [];
      if (event.sharedGroupName) eventGroupNames.push(event.sharedGroupName);
      if (event.groups && Array.isArray(event.groups)) {
        eventGroupNames.push(...event.groups);
      }
      
      return eventGroupNames.some((group) => selectedEventGroups.has(group));
    });

    // Filter events by selected tracks
    const filteredEvents = groupFilteredEvents.filter((event) => {
      if (selectedEventTracks.size === 0) return true;
      const trackName = (event.track || event.trackName || "").trim();
      return trackName && selectedEventTracks.has(trackName);
    });

    filteredEvents.forEach((event) => {
      if (!event.racers || !Array.isArray(event.racers)) return;

      // Get finished racers for this event
      const finishedRacers = event.racers
        .filter((r) => r.duration !== null && r.duration !== undefined)
        .sort((a, b) => a.duration - b.duration);

      // Get winning time (best time in the event)
      const winningTime = finishedRacers.length > 0 ? finishedRacers[0].duration : null;
      
      // Calculate median time
      let medianTime = null;
      if (finishedRacers.length > 0) {
        const sortedDurations = finishedRacers.map(r => r.duration).sort((a, b) => a - b);
        const mid = Math.floor(sortedDurations.length / 2);
        medianTime = sortedDurations.length % 2 === 0
          ? (sortedDurations[mid - 1] + sortedDurations[mid]) / 2
          : sortedDurations[mid];
      }

      // Get results for selected racers
      const racerResults = racers.map((racer) => {
        const racerEntry = event.racers.find((r) => r.racerId === racer.id);
        if (!racerEntry || !racerEntry.duration) {
          return {
            racerId: racer.id,
            racerName: racer.name,
            duration: null,
            position: null,
            finished: false,
            percentFromWinner: null,
            percentFromMedian: null,
          };
        }

        const position = finishedRacers.findIndex((r) => r.racerId === racer.id) + 1;
        const percentFromWinner = winningTime
          ? ((racerEntry.duration - winningTime) / winningTime) * 100
          : null;
        const percentFromMedian = medianTime
          ? ((racerEntry.duration - medianTime) / medianTime) * 100
          : null;

        return {
          racerId: racer.id,
          racerName: racer.name,
          duration: racerEntry.duration,
          position,
          finished: true,
          percentFromWinner,
          percentFromMedian,
        };
      });

      const selectedFinishedDurations = racerResults
        .filter((result) => result.finished)
        .map((result) => result.duration)
        .sort((a, b) => a - b);

      let selectedMedianTime = null;
      if (selectedFinishedDurations.length > 0) {
        const mid = Math.floor(selectedFinishedDurations.length / 2);
        selectedMedianTime = selectedFinishedDurations.length % 2 === 0
          ? (selectedFinishedDurations[mid - 1] + selectedFinishedDurations[mid]) / 2
          : selectedFinishedDurations[mid];
      }

      const eventDate = event.createdAt?.seconds
        ? new Date(event.createdAt.seconds * 1000)
        : event.createdAt
        ? new Date(event.createdAt)
        : null;

      sharedEvents.push({
        eventId: event.id,
        eventName: event.name,
        date: event.createdAt,
        eventDate,
        racerResults,
        winningTime,
        medianTime,
        selectedMedianTime,
        sharedGroupName: event.sharedGroupName,
        groups: event.groups,
      });

      // Update stats and chart data
      racerResults.forEach((result) => {
        if (result.finished) {
          const racerStats = stats[result.racerId];
          racerStats.totalEvents++;
          racerStats.totalPosition += result.position;
          racerStats.totalTime += result.duration;

          if (racerStats.bestTime === null || result.duration < racerStats.bestTime) {
            racerStats.bestTime = result.duration;
          }
          if (racerStats.bestPosition === null || result.position < racerStats.bestPosition) {
            racerStats.bestPosition = result.position;
          }

          // Add chart data point
          if (eventDate) {
            chartDataPoints[result.racerId].push({
              x: eventDate,
              y: result.percentFromWinner,
              yMedian: result.percentFromMedian,
            });
          }
        }
      });

      // Calculate wins/losses/ties
      const finishedResults = racerResults.filter((r) => r.finished);
      if (finishedResults.length >= 2) {
        const bestPosition = Math.min(...finishedResults.map((r) => r.position));
        const racersAtBestPosition = finishedResults.filter((r) => r.position === bestPosition);

        racersAtBestPosition.forEach((result) => {
          stats[result.racerId].wins++;
        });

        if (racersAtBestPosition.length > 1) {
          racersAtBestPosition.forEach((result) => {
            stats[result.racerId].ties += racersAtBestPosition.length - 1;
          });
        }

        finishedResults.forEach((result) => {
          if (result.position > bestPosition) {
            stats[result.racerId].losses++;

            const tiedAtThisPosition = finishedResults.filter(
              (r) => r.position === result.position
            );
            if (tiedAtThisPosition.length > 1) {
              stats[result.racerId].ties += tiedAtThisPosition.length - 1;
            }
          }
        });
      }
    });

    // Calculate averages
    Object.keys(stats).forEach((racerId) => {
      const racerStats = stats[racerId];
      if (racerStats.totalEvents > 0) {
        racerStats.averagePosition = racerStats.totalPosition / racerStats.totalEvents;
        racerStats.averageTime = racerStats.totalTime / racerStats.totalEvents;
      }
    });

    // Sort shared events by date (most recent first)
    sharedEvents.sort((a, b) => {
      const aDate = a.date?.seconds || a.date || 0;
      const bDate = b.date?.seconds || b.date || 0;
      return bDate - aDate;
    });

    // Prepare chart data
    const sortedEvents = [...sharedEvents].sort((a, b) => {
      const aDate = a.eventDate?.getTime() || 0;
      const bDate = b.eventDate?.getTime() || 0;
      return aDate - bDate;
    });

    const chartLabels = sortedEvents.map((event) => {
      if (!event.eventDate) return "";
      return event.eventDate.toLocaleDateString();
    });

    const colors = [
      "rgb(19, 161, 168)", // turquoise
      "rgb(254, 108, 98)", // red
      "rgb(255, 193, 7)", // yellow
      "rgb(40, 167, 69)", // green
      "rgb(0, 123, 255)", // blue
      "rgb(108, 117, 125)", // gray
    ];

    const chartDatasets = chartMetric === "median-absolute"
      ? [
          {
            label: "Median Time (Selected Racers)",
            data: sortedEvents.map((event) => event.selectedMedianTime),
            borderColor: colors[0],
            backgroundColor: colors[0] + "20",
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ]
      : chartMetric === "median-winning"
      ? [
          {
            label: "Median (Selected) % from Winner",
            data: sortedEvents.map((event) => {
              if (!event.selectedMedianTime || !event.winningTime) return null;
              return ((event.selectedMedianTime - event.winningTime) / event.winningTime) * 100;
            }),
            borderColor: colors[0],
            backgroundColor: colors[0] + "20",
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ]
      : chartMetric === "median-median"
      ? [
          {
            label: "Median (Selected) % over Event Median",
            data: sortedEvents.map((event) => {
              if (!event.selectedMedianTime || !event.medianTime) return null;
              return ((event.selectedMedianTime - event.medianTime) / event.medianTime) * 100;
            }),
            borderColor: colors[0],
            backgroundColor: colors[0] + "20",
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ]
      : racers.map((racer, index) => {
          const data = sortedEvents.map((event) => {
            const result = event.racerResults.find((r) => r.racerId === racer.id);
            if (!result) return null;
            return chartMetric === "winning"
              ? (result.percentFromWinner !== null ? result.percentFromWinner : null)
              : (result.percentFromMedian !== null ? result.percentFromMedian : null);
          });

          return {
            label: racer.name,
            data,
            borderColor: colors[index % colors.length],
            backgroundColor: colors[index % colors.length] + "20",
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
          };
        });

      const chartData = {
      labels: chartLabels,
      datasets: chartDatasets,
    };

    return { sharedEvents, stats, chartData };
  }, [racers, events, selectedEventGroups, selectedEventTracks, chartMetric]);

  const racerHasResults = useMemo(() => {
    const map = {};
    racers.forEach((racer) => {
      map[racer.id] = false;
    });
    events.forEach((event) => {
      if (!event.racers || !Array.isArray(event.racers)) return;
      event.racers.forEach((entry) => {
        if (
          entry &&
          entry.racerId &&
          entry.duration !== null &&
          entry.duration !== undefined &&
          map[entry.racerId] !== undefined
        ) {
          map[entry.racerId] = true;
        }
      });
    });
    return map;
  }, [events, racers]);

  const formatDuration = (ms) => {
    if (!ms) return "-";
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, "0")}.${milliseconds.toString().padStart(2, "0")}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const handleRemoveRacer = (racerId) => {
    const newRacerIds = racerIdArray.filter((id) => id !== racerId);
    if (newRacerIds.length < 2) {
      showToast("You need at least 2 racers to compare.");
      return;
    }
    navigate(`/compare/${newRacerIds.join(",")}`);
  };

  const handleAddRacer = (racerId) => {
    if (racerIdArray.includes(racerId)) {
      showToast("This racer is already in the comparison.");
      return;
    }
    const newRacerIds = [...racerIdArray, racerId];
    navigate(`/compare/${newRacerIds.join(",")}`);
    setShowAddRacerDropdown(false);
  };

  const handleToggleEventGroup = (groupName) => {
    setSelectedEventGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const handleToggleEventTrack = (trackName) => {
    setSelectedEventTracks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(trackName)) {
        newSet.delete(trackName);
      } else {
        newSet.add(trackName);
      }
      return newSet;
    });
  };

  // Restore persisted filters when user changes
  useLayoutEffect(() => {
    setFiltersRestored(false);
    try {
      const userId = user?.uid || null;
      const savedFilters = loadRacerGroupFilters(userId);
      setSelectedGroupFilters(savedFilters);
    } catch (error) {
      console.error("Failed to restore compare racers filters:", error);
    }
    setFiltersRestored(true);
  }, [user?.uid]);

  // Persist filters when they change
  useEffect(() => {
    if (!filtersRestored) return;
    try {
      const userId = user?.uid || null;
      saveRacerGroupFilters(userId, selectedGroupFilters);
    } catch (error) {
      console.error("Failed to persist compare racers filters:", error);
    }
  }, [selectedGroupFilters, user?.uid, filtersRestored]);

  // Toggle group filter
  const toggleGroupFilter = (groupName) => {
    setSelectedGroupFilters(prev => {
      if (prev.includes(groupName)) {
        return prev.filter(g => g !== groupName);
      } else {
        return [...prev, groupName];
      }
    });
  };

  // Clear all group filters
  const clearGroupFilters = () => {
    setSelectedGroupFilters([]);
  };

  // Get distinct groups from available racers
  const distinctGroups = useMemo(() => {
    const groupsSet = new Set();
    allRacers.forEach(racer => {
      if (racer.groups) {
        racer.groups.forEach(g => groupsSet.add(g));
      }
    });
    return Array.from(groupsSet).sort();
  }, [allRacers]);

  // Filter available racers by group and exclude already selected racers
  const availableRacers = useMemo(() => {
    return allRacers
      .filter((racer) => !racerIdArray.includes(racer.id))
      .filter((racer) => {
        if (selectedGroupFilters.length === 0) return true;
        return racer.groups && racer.groups.some(group => selectedGroupFilters.includes(group));
      });
  }, [allRacers, racerIdArray, selectedGroupFilters]);

  // Apply pending chart metric after a delay to allow dropdown to finish
  useEffect(() => {
    if (pendingChartMetric !== null) {
      const timer = setTimeout(() => {
        setChartMetric(pendingChartMetric);
        setPendingChartMetric(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingChartMetric]);

  // Chart component
  const ChartComponent = ({ chartData, chartMetric }) => {
    const chartCanvasRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const chartDataRef = useRef(null);

    useEffect(() => {
      if (!chartCanvasRef.current || !chartData) return;

      const nextDataKey = JSON.stringify(chartData);
      if (chartDataRef.current === nextDataKey && chartInstanceRef.current) {
        return;
      }
      chartDataRef.current = nextDataKey;

      if (chartInstanceRef.current) {
        chartInstanceRef.current.data = chartData;
        chartInstanceRef.current.options.plugins.title.text =
          chartMetric === "winning"
            ? "% Difference from Winning Time Over Time"
            : chartMetric === "median"
            ? "% Time Over Median Time Over Time"
            : chartMetric === "median-absolute"
            ? "Median Time (Selected Racers)"
            : chartMetric === "median-winning"
            ? "Median (Selected) % from Winner"
            : "Median (Selected) % over Event Median";
        chartInstanceRef.current.options.scales.y.title.text =
          chartMetric === "winning"
            ? "% Difference from Winner"
            : chartMetric === "median"
            ? "% Over Median Time"
            : chartMetric === "median-absolute"
            ? "Median Time"
            : chartMetric === "median-winning"
            ? "% from Winner"
            : "% over Event Median";
        chartInstanceRef.current.update("none");
        return;
      }

      const ctx = chartCanvasRef.current.getContext("2d");
      chartInstanceRef.current = new Chart(ctx, {
        type: "line",
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0,
          },
          plugins: {
            legend: {
              position: "top",
            },
            title: {
              display: true,
              text: chartMetric === "winning"
                ? "% Difference from Winning Time Over Time"
                : chartMetric === "median"
                ? "% Time Over Median Time Over Time"
                : chartMetric === "median-absolute"
                ? "Median Time (Selected Racers)"
                : chartMetric === "median-winning"
                ? "Median (Selected) % from Winner"
                : "Median (Selected) % over Event Median",
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  if (context.parsed.y === null) return "";
                  if (chartMetric === "median-absolute") {
                    return `${context.dataset.label}: ${formatDuration(context.parsed.y)}`;
                  }
                  return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: chartMetric === "winning"
                  ? "% Difference from Winner"
                  : chartMetric === "median"
                  ? "% Over Median Time"
                  : chartMetric === "median-absolute"
                  ? "Median Time"
                  : chartMetric === "median-winning"
                  ? "% from Winner"
                  : "% over Event Median",
              },
              ticks: {
                callback: function (value) {
                  if (chartMetric === "median-absolute") {
                    return formatDuration(value);
                  }
                  return value.toFixed(1) + "%";
                },
              },
            },
            x: {
              title: {
                display: true,
                text: "Event Date",
              },
            },
          },
        },
      });

    }, [chartData, chartMetric]);

    useEffect(() => {
      return () => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
      };
    }, []);

    return (
      <div
        style={{
          padding: "20px",
          border: "1px solid #ccc",
          borderRadius: "5px",
          backgroundColor: "#fff",
          position: "relative",
          height: "400px",
          marginBottom: "20px",
        }}
      >
        <canvas ref={chartCanvasRef}></canvas>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ width: "100%", padding: "30px 20px", textAlign: "center", boxSizing: "border-box", maxWidth: "100%" }}>
        <p>Loading comparison...</p>
      </div>
    );
  }

  if (racers.length < 2) {
    return (
      <div style={{ width: "100%", padding: "30px 20px", boxSizing: "border-box", maxWidth: "100%" }}>
        <button
          onClick={() => navigate("/racers-list")}
          style={{
            padding: "8px 15px",
            backgroundColor: "#13A1A8",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            borderRadius: "3px",
            marginBottom: "20px",
          }}
        >
          <FaArrowLeft /> Back to Racers List
        </button>
        <p>Please select at least 2 racers to compare.</p>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", padding: "30px 20px", boxSizing: "border-box", maxWidth: "100%" }}>
      <div className="homepage-other-tools">
        {/* Header */}
        <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
          <button
            onClick={() => navigate("/racers-list")}
            style={{
              padding: "8px",
              backgroundColor: "transparent",
              border: "none",
              color: "#000",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaChevronLeft size={20} />
          </button>
          <h1>Compare Racers</h1>
        </div>

        {/* Racer Names with Remove buttons */}
        <div style={{ marginBottom: "20px", display: "flex", gap: "15px", flexWrap: "wrap", alignItems: "center" }}>
          {racers.map((racer) => {
            const isDisabled = !racerHasResults[racer.id];
            return (
              <div
                key={racer.id}
                style={{
                  padding: "10px 15px",
                  backgroundColor: isDisabled ? "#f5f5f5" : "#f0f8ff",
                  borderRadius: "5px",
                  border: isDisabled ? "2px solid #ddd" : "2px solid #13A1A8",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  color: isDisabled ? "#999" : "#000",
                  opacity: isDisabled ? 0.7 : 1,
                }}
              >
                <span>
                  {racer.name}
                  {isDisabled && (
                    <span style={{ marginLeft: "6px", fontWeight: "normal" }}>(no results)</span>
                  )}
                </span>
                <button
                  onClick={() => !isDisabled && handleRemoveRacer(racer.id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    color: isDisabled ? "#bbb" : "#dc3545",
                    padding: "0",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title={isDisabled ? "No results to compare" : "Remove racer"}
                  disabled={isDisabled}
                >
                  <FaTimes />
                </button>
              </div>
            );
          })}
          {/* Add Racer Button */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowAddRacerDropdown(!showAddRacerDropdown)}
              style={{
                padding: "10px 15px",
                backgroundColor: "#13A1A8",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                borderRadius: "5px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <FaPlus /> Add Racer
            </button>
            {showAddRacerDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: "5px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                  maxHeight: "400px",
                  overflowY: "auto",
                  minWidth: "250px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Group filter dropdown */}
                {distinctGroups.length > 0 && (
                  <div style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                    <GroupFilterDropdown
                      distinctGroups={distinctGroups}
                      selectedGroupFilters={selectedGroupFilters}
                      onToggleGroupFilter={toggleGroupFilter}
                      onClearGroupFilters={clearGroupFilters}
                      label="Filter by group:"
                    />
                  </div>
                )}
                {/* Racers list */}
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {availableRacers.length === 0 ? (
                    <div style={{ padding: "10px", color: "#666" }}>
                      {selectedGroupFilters.length > 0 
                        ? "No racers match the selected filters" 
                        : "No more racers available"}
                    </div>
                  ) : (
                    availableRacers.map((racer) => (
                      <div
                        key={racer.id}
                        onClick={() => handleAddRacer(racer.id)}
                        style={{
                          padding: "10px 15px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = "#f0f8ff")}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                      >
                        <div style={{ fontWeight: "500" }}>{racer.name}</div>
                        {racer.groups && racer.groups.length > 0 && (
                          <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                            {racer.groups.join(", ")}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Group Filter */}
        <div style={{ marginBottom: "20px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {eventGroups.length > 0 && (
            <FilterDropdown
              label="Filter by Event Group:"
              title="Filter by event group"
              dropdownTitle="Filter Event Groups"
              searchPlaceholder="Search event groups..."
              emptyText="No event groups found"
              options={eventGroups}
              selectedValues={Array.from(selectedEventGroups)}
              onToggle={handleToggleEventGroup}
              onClear={() => setSelectedEventGroups(new Set())}
              buttonText="Filter"
              activeBackgroundColor="#6c757d"
              inactiveTextColor="#333"
              isOpen={showGroupFilter}
              onOpenChange={setShowGroupFilter}
              enableSearch
            />
          )}
          {eventTracks.length > 0 && (
            <FilterDropdown
              label="Filter by Track:"
              title="Filter by track"
              dropdownTitle="Filter Tracks"
              searchPlaceholder="Search tracks..."
              emptyText="No tracks found"
              options={eventTracks}
              selectedValues={Array.from(selectedEventTracks)}
              onToggle={handleToggleEventTrack}
              onClear={() => setSelectedEventTracks(new Set())}
              buttonText="Filter"
              activeBackgroundColor="#6c757d"
              inactiveTextColor="#333"
              isOpen={showTrackFilter}
              onOpenChange={setShowTrackFilter}
              enableSearch
            />
          )}
        </div>

        {/* Chart */}
        {comparisonData.chartData && comparisonData.sharedEvents.length > 0 && (
          <div style={{ marginBottom: "50px" }}>
            <div style={{ marginBottom: "15px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
              <h2 style={{ margin: 0 }}>
                <FaChartLine style={{ marginRight: "8px" }} />
                Evolution: {chartMetric === "winning" ? "% Difference from Winning Time" : "% Time Over Median Time"}
              </h2>
              <div 
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <label style={{ fontSize: "0.9em", fontWeight: "500" }}>Metric:</label>
                <select
                  value={pendingChartMetric !== null ? pendingChartMetric : chartMetric}
                  onChange={(e) => {
                    e.stopPropagation();
                    const newValue = e.target.value;
                    // Set pending value immediately for UI feedback, apply to chart after delay
                    setPendingChartMetric(newValue);
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    e.stopPropagation();
                    // Apply immediately on blur if there's a pending value
                    if (pendingChartMetric !== null) {
                      setChartMetric(pendingChartMetric);
                      setPendingChartMetric(null);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontSize: "0.9em",
                    zIndex: 1000,
                    position: "relative",
                    isolation: "isolate",
                  }}
                >
                  <option value="winning">% Difference from Winning Time</option>
                  <option value="median">% Time Over Median Time</option>
                  <option value="median-absolute">Median Time (Selected Racers)</option>
                  <option value="median-winning">Median (Selected) % from Winner</option>
                  <option value="median-median">Median (Selected) % over Event Median</option>
                </select>
              </div>
            </div>
            <ChartComponent chartData={comparisonData.chartData} chartMetric={chartMetric} />
          </div>
        )}

        {/* Summary Stats */}
        {comparisonData.sharedEvents.length > 0 && (
          <div style={{ marginBottom: "30px", marginTop: "20px" }}>
            <h2 style={{ marginBottom: "15px" }}>
              <FaChartLine style={{ marginRight: "8px" }} />
              Summary Statistics
            </h2>
            <div
              style={{
                overflowX: "auto",
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #ccc" }}>
                    <th style={{ padding: "10px", textAlign: "left" }}>Racer</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Events</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>
                      <FaTrophy style={{ color: "#FFD700" }} /> Wins
                    </th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Losses</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Ties</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Avg Position</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Best Position</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Avg Time</th>
                    <th style={{ padding: "10px", textAlign: "center" }}>Best Time</th>
                  </tr>
                </thead>
                <tbody>
                  {racers.map((racer) => {
                    const stats = comparisonData.stats[racer.id] || {};
                    return (
                      <tr key={racer.id} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "10px", fontWeight: "bold" }}>{racer.name}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {stats.totalEvents || 0}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center", color: "#28a745", fontWeight: "bold" }}>
                          {stats.wins || 0}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center", color: "#dc3545" }}>
                          {stats.losses || 0}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{stats.ties || 0}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {stats.averagePosition
                            ? stats.averagePosition.toFixed(1)
                            : "-"}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {stats.bestPosition || "-"}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {stats.averageTime ? formatDuration(stats.averageTime) : "-"}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center", fontWeight: "bold" }}>
                          {stats.bestTime ? formatDuration(stats.bestTime) : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Head-to-Head Results */}
        <div>
          <h2 style={{ marginBottom: "15px" }}>Head-to-Head Results</h2>
          {comparisonData.sharedEvents.length === 0 ? (
            <div
              style={{
                padding: "30px",
                textAlign: "center",
                backgroundColor: "#f9f9f9",
                borderRadius: "5px",
                border: "1px solid #ddd",
              }}
            >
              <p style={{ fontSize: "1.1em", color: "#666" }}>
                {selectedEventGroups.size > 0
                  ? "No events found matching the selected filters."
                  : "No shared events found. These racers have not all competed in the same events yet."}
              </p>
            </div>
          ) : (
            <div
              style={{
                overflowX: "auto",
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #ccc", backgroundColor: "#f5f5f5" }}>
                    <th style={{ padding: "10px", textAlign: "left" }}>Event</th>
                    <th style={{ padding: "10px", textAlign: "left" }}>Date</th>
                    {racers.map((racer) => (
                      <th
                        key={racer.id}
                        style={{ padding: "10px", textAlign: "center", minWidth: "150px" }}
                      >
                        {racer.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.sharedEvents.map((event) => {
                    const finishedResults = event.racerResults.filter((r) => r.finished);
                    const winnerPosition = finishedResults.length > 0
                      ? Math.min(...finishedResults.map((r) => r.position))
                      : null;

                    return (
                      <tr key={event.eventId} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "10px" }}>{event.eventName}</td>
                        <td style={{ padding: "10px" }}>{formatDate(event.date)}</td>
                        {event.racerResults.map((result) => {
                          const isWinner = result.finished && result.position === winnerPosition;
                          const isLoser = result.finished && result.position > winnerPosition;

                          return (
                            <td
                              key={result.racerId}
                              style={{
                                padding: "10px",
                                textAlign: "center",
                                backgroundColor: isWinner
                                  ? "#d4edda"
                                  : isLoser
                                  ? "#f8d7da"
                                  : result.finished
                                  ? "#fff3cd"
                                  : "transparent",
                                fontWeight: isWinner ? "bold" : "normal",
                              }}
                            >
                              {result.finished ? (
                                <>
                                  <div style={{ fontSize: "0.9em", color: "#666" }}>
                                    Position: {result.position}
                                  </div>
                                  <div style={{ fontSize: "1em", marginTop: "5px" }}>
                                    {formatDuration(result.duration)}
                                  </div>
                                  {result.percentFromWinner !== null && (
                                    <div style={{ fontSize: "0.85em", color: "#666", marginTop: "3px" }}>
                                      {result.percentFromWinner > 0 ? "+" : ""}
                                      {result.percentFromWinner.toFixed(2)}%
                                    </div>
                                  )}
                                  {isWinner && (
                                    <div style={{ marginTop: "5px" }}>
                                      <FaTrophy style={{ color: "#FFD700" }} />
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: "#999" }}>Did not finish</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareRacers;
